import { Button } from "@/components/ui/button";
import { Menu, Zap, Gift } from "lucide-react";
import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/hooks/useAppSettings";
import { NotificationCenter } from "@/components/NotificationCenter";
import { CreditsBadge } from "@/components/CreditsBadge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { usePrefetchTools } from "@/hooks/useToolsCache";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: appSettings } = useAppSettings();
  
  // Admin check: only use database-verified admin status for security
  const isAdmin = !!(profile?.is_admin);
  const { prefetchAll } = usePrefetchTools();
  
  // Prefetch tools ao hover no link de ferramentas
  const handleFerramentasHover = useCallback(() => {
    prefetchAll();
  }, [prefetchAll]);

  return (
    <header id="tour-header" className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-soft">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">MarketPro Tools</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-foreground hover:text-primary hover:font-bold hover:scale-105 transition-all duration-200">
              Início
            </Link>
            <Link 
              to="/ferramentas" 
              className="text-foreground hover:text-primary hover:font-bold hover:scale-105 transition-all duration-200"
              onMouseEnter={handleFerramentasHover}
            >
              Ferramentas
            </Link>
            <Link to="/desafios" className="text-foreground hover:text-primary hover:font-bold hover:scale-105 transition-all duration-200">
              Desafios
            </Link>
            <Link to="/planos" className="text-foreground hover:text-primary hover:font-bold hover:scale-105 transition-all duration-200">
              Planos
            </Link>
            <Link 
              to="/auth?trial=true" 
              className="flex items-center gap-1.5 text-primary font-semibold hover:text-primary/80 hover:scale-105 transition-all duration-200 bg-primary/10 px-3 py-1.5 rounded-full"
            >
              <Gift className="w-4 h-4" />
              Teste Grátis 7 Dias
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <ThemeToggle />
            {user && !appSettings?.hideTokensDisplay && <CreditsBadge variant="compact" />}
            {user && <NotificationCenter />}
            {user ? (
              <>
                <span className="text-sm font-medium text-foreground">
                  {profile?.nome || user.email}
                </span>
                <Link to="/dashboard">
                  <Button variant="outline" size="sm">
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={async () => {
                    await signOut();
                    navigate('/');
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" size="sm">
                    Entrar
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="cta" size="sm">
                    Começar Agora
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-border bg-white/95 backdrop-blur-sm">
            <nav className="flex flex-col gap-4">
              <Link to="/" className="text-foreground hover:text-primary hover:font-bold transition-all py-2">
                Início
              </Link>
              <Link to="/ferramentas" className="text-foreground hover:text-primary hover:font-bold transition-all py-2">
                Ferramentas
              </Link>
              <Link to="/desafios" className="text-foreground hover:text-primary hover:font-bold transition-all py-2">
                Desafios
              </Link>
              <Link to="/planos" className="text-foreground hover:text-primary hover:font-bold transition-all py-2">
                Planos
              </Link>
              <Link 
                to="/auth?trial=true" 
                className="flex items-center gap-1.5 text-primary font-semibold hover:text-primary/80 transition-all py-2"
              >
                <Gift className="w-4 h-4" />
                Teste Grátis 7 Dias
              </Link>
              <div className="flex flex-col gap-2 pt-4">
                {user ? (
                  <>
                    <span className="text-sm font-medium text-foreground text-center py-2">
                      {profile?.nome || user.email}
                    </span>
                    <Link to="/dashboard" className="w-full">
                      <Button variant="outline" size="sm" className="w-full">
                        Dashboard
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full"
                      onClick={async () => {
                        await signOut();
                        navigate('/');
                      }}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/auth" className="w-full">
                      <Button variant="ghost" size="sm" className="w-full">
                        Entrar
                      </Button>
                    </Link>
                    <Link to="/auth" className="w-full">
                      <Button variant="cta" size="sm">
                        Começar Agora
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;